import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ApiClientService} from '../../../services/api-client.service';
import {UrlService} from '../../../services/url.service';
import {ResidentForSelect, ReportGenerator, ReportTemplate} from '../../../types/data.types';
import {ReportGeneratorService} from '../../../services/report-generator.service';
import {DragulaService} from 'ng2-dragula';
import {ConstantsService} from '../../../services/constants.service';

declare const toastr: any;
declare const $: any;
declare const swal: any;
declare const moment: any;

@Component({
    selector: 'report-generator-modal',
    templateUrl: './report-generator-modal.component.html',
    styleUrls: ['./report-generator-modal.component.scss']
})
export class ReportGeneratorModalComponent implements OnInit, OnDestroy {
    @ViewChild('form') form;
    showModal = false;
    suites: Array<any> = [];
    residents: Array<ResidentForSelect> = [];
    disabledSubmit = false;
    buildingSelectIsDisabled = false;
    sections: Array<any> = [];
    chosenSection: any;
    data: ReportGenerator = new ReportGenerator();
    somethingWasMoved = false;
    newCondition = {
        key: '',
        condition: '',
        value: '',
        isRuntime: false
    };
    showSorting = false;
    showConditions = false;
    possibleValues = [];

    static parseTime(dateString: string): string {
        return (new DatePipe('en-US')).transform(dateString, 'hh:mm a');
    }

    constructor(
        public api: ApiClientService,
        public reportGeneratorService: ReportGeneratorService,
        public urlService: UrlService,
        private dragulaService: DragulaService
    ) {}

    ngOnInit() {
        this.initData();
        setTimeout( () => this.showModal = true, 300);
        this.dragulaService.drop.subscribe((value) => this.somethingWasMoved = true);
        this.dragulaService.setOptions('bag-fields', {
            moves: (el: any, container: any, handle: any) => { return !this.reportGeneratorService.viewOnlyMode; }
        });
        this.urlService.modalOpened.emit();
    }

    initData() {
        if(this.reportGeneratorService.templateInTemplateModal) {
            const data = JSON.parse(JSON.stringify(this.reportGeneratorService.templateInTemplateModal));
            this.data.id = data.id;
            this.data.name = this.reportGeneratorService.copyingReport ? '' : data.name;
            this.data.description = this.reportGeneratorService.copyingReport ? '' : data.description;
            this.data.section = data.section;
            this.data.type = data.type;
            this.data.orderBy = data.orderBy ? data.orderBy.split('|')[0] : '';
            this.data.orderByDirection = data.orderBy ? data.orderBy.split('|')[1] : '';
            this.data.conditions = data.conditions.map(cond => ({
                key: cond.key,
                condition: cond.condition,
                value: cond.values.length > 0 ? cond.values : '',
                isRuntime: cond.isRuntime
            }));
            this.data.filters = data.filters;
            this.showSorting = (this.data.orderBy || this.data.orderByDirection) ? true : false;
        }
        this.chosenSection = {
            fields: [],
            conditions: []
        };
        this.getSections();
    }

    hideModal() {
        if ((this.form.dirty && this.form.touched) || this.somethingWasMoved) {
            swal(ConstantsService.getSwalConfig('Close without saving?', 'Do you want to delete unsaved data?'),
                () => {
                    this.reportGeneratorService.templateInTemplateModal = null;
                    this.reportGeneratorService.turnComponent('isListShow');
                    this.reportGeneratorService.viewOnlyMode = false;
                    this.urlService.modalClosed.emit();
                }
            );
        } else {
            this.reportGeneratorService.turnComponent('isListShow');
            this.reportGeneratorService.viewOnlyMode = false;
            this.urlService.modalClosed.emit();
        }
    }

    updateView(msg: string, run): void {
        toastr.success(msg);
        this.reportGeneratorService.tabId = run ? 'allReports' : 'allTemplates';
        if(!run)
            this.reportGeneratorService.templateInTemplateModal = null;
        this.reportGeneratorService.turnComponent('isListShow');
        this.reportGeneratorService.getData();
    }

    saveReport(run) {
        if (!this.form.valid) {
            if (!this.form.value.reportName) toastr.warning('Name should not be blank.');
            if (!this.form.value.section) toastr.warning('Section should not be blank.');
            return;
        }
        if ((!this.form.value.orderBy && this.form.value.orderByDirection) || (this.form.value.orderBy && !this.form.value.orderByDirection)) {
            toastr.warning('Order By and Order By Direction both should not be blank to set up ordering.');
            return;
        }

        if (this.data.fieldsToShow.length < 1) {
            toastr.warning('Fields to show should not be empty.');
            return;
        }

        const data = {
            name: this.data.name,
            description: this.data.description,
            section: this.data.section,
            type: this.data.type,
            fields: this.getFieldsForSending(this.data.fieldsToShow),
            filters: this.data.filters,
            groupsBy: this.getFieldsForSending(this.data.groupBy),
            orderBy: this.data.orderBy && this.data.orderByDirection ? (this.data.orderBy + '|' + this.data.orderByDirection) : undefined,
            conditions: this.getConditionsForSending()
        };

        if (this.newCondition.key || this.newCondition.condition || this.newCondition.value) {
            swal(ConstantsService.getSwalConfig('Close without adding?', 'Your condition wasn\'t added. Are you sure you want to save the report template without the condition?'),
                () => this.sendTemplateInfo(run, data)
            );
        } else
            this.sendTemplateInfo(run, data);
    }

    sendTemplateInfo(run, data) {
        this.disabledSubmit = true;
        if (this.data.id && !this.reportGeneratorService.copyingReport) {
            const url = `/v1/report_templates/${this.data.id}/replace.json`;
            this.reportGeneratorService.api.post('domainReportGenerator', url, data).subscribe(
                res => {
                    if(run) {
                        this.reportGeneratorService.templateInTemplateModal = new ReportTemplate();
                        this.reportGeneratorService.templateInTemplateModal.id = res.id;
                        this.reportGeneratorService.templateInTemplateModal.name = this.data.name;
                        this.reportGeneratorService.showReportNameModal = true;
                    }
                    this.updateView('Report Template has been successfully updated', run);
                },
                error => {
                    this.disabledSubmit = false;
                    this.api.errorHandler(error, 'Failed to update Report Template');
                }
            );
        } else {
            this.reportGeneratorService.api.post('domainReportGenerator', '/v1/report_templates.json', data).subscribe(
                res => {
                    if(run) {
                        this.reportGeneratorService.templateInTemplateModal = new ReportTemplate();
                        this.reportGeneratorService.templateInTemplateModal.id = res.id;
                        this.reportGeneratorService.templateInTemplateModal.name = this.data.name;
                        this.reportGeneratorService.showReportNameModal = true;
                    }
                    this.updateView('Report Template has been successfully created', run);
                },
                error => {
                    this.disabledSubmit = false;
                    this.api.errorHandler(error, 'Failed to create Report Template');
                }
            );
        }
    }

    getSections() {
        this.sections = [];
        const url = '/v1/report_sections.json';
        this.api.get('domainReportGenerator', url).subscribe(
            (res) => {
                const body = JSON.parse(res._body);
                this.sections = body.entities;
                if(this.reportGeneratorService.templateInTemplateModal) {
                    this.chosenSection = JSON.parse(JSON.stringify(this.sections.filter(section => section.id == this.data.section)[0]));
                    let fields = [];
                    let groupByFields = [];
                    (<any>this.reportGeneratorService.templateInTemplateModal).fields.forEach( f => {
                        this.chosenSection.fields.forEach((field, index) => {
                            if(field.key == f) {
                                fields.push(field);
                                this.chosenSection.fields.splice(index, 1);
                            }
                        });
                    });
                    (<any>this.reportGeneratorService.templateInTemplateModal).groupsBy.forEach( group => {
                        this.chosenSection.fields.forEach((field, index) => {
                            if(field.key == group) {
                                groupByFields.push(field);
                                this.chosenSection.fields.splice(index, 1)
                            }
                        });
                    });
                    this.data.fieldsToShow = fields;
                    this.data.groupBy = groupByFields;
                }
            },
            (error) => this.api.errorHandler(error, 'Failed to get Sections')
        );
    }

    onSectionChange(e) {
        this.newCondition = {
            key: '',
            condition: '',
            value: '',
            isRuntime: false
        };
        this.data.conditions = [];
        this.data.fieldsToShow = [];
        this.data.groupBy = [];
        this.data.orderBy = '';
        this.data.orderByDirection = '';
        this.chosenSection = JSON.parse(JSON.stringify(this.sections.filter(section => section.id == e.target.value)[0]));
        this.reportGeneratorService.modalInfoWasChanged = true;
    }

    getFieldsForSending(array): Array<string> {
        return array.map(field => (field.key));
    }

    getConditions() {
        let conditions = [];
        if(this.newCondition.key)
            conditions = this.data.fieldsToShow.filter(cond => this.newCondition.key == cond.key)[0].conditions;
        return conditions;
    }

    getFieldType() {
        let condition = null;
        if(this.newCondition.condition && this.getConditions().length > 0) {
            condition = this.getConditions().find(condition => this.newCondition.condition == condition.id);
            if(condition && condition.inputType == 'string') {
                const possibleValues = this.data.fieldsToShow.find(cond => this.newCondition.key == cond.key).possibleValues;
                if(possibleValues && possibleValues.length > 0)
                    return 'possibleValues';
            }

        }
        return condition ? condition.inputType : null;
    }

    getPossibleValues() {
        const values = this.data.fieldsToShow.find(cond => this.newCondition.key == cond.key).possibleValues;
        return values.map(v => ({
                   id: v,
                   itemName: v
               }));
    }

    addNewCondition() {
        if(this.newCondition.key && this.newCondition.condition &&
           (this.newCondition.value || (this.getFieldType() === '' && !this.newCondition.value))) {
            if(this.getFieldType() === 'buildings')
                (<any>this.newCondition).value = (<any>this.newCondition).value.map(v => (v.id));
            else if(this.getFieldType() === 'possibleValues')
                (<any>this.newCondition).value = (<any>this.newCondition).value.map(v => (v.itemName));
            this.data.conditions.push(this.newCondition);
            this.newCondition = {
                key: '',
                condition: '',
                value: '',
                isRuntime: false
            };
            this.reportGeneratorService.modalInfoWasChanged = true;
        } else
            toastr.warning('All condition fields must be filled to add new condition.');
    }

    removeCondition(index) {
        this.data.conditions.splice(index, 1);
    }

    outputFormatting(value) {
        let inputType = '';
        this.sections.forEach(section => {
            if(section.id == this.chosenSection.id) {
                const field = section.fields.find(field => field.key == value.key);
                if(field) {
                    const condition = field.conditions.find(condition => (condition.id == value.condition));
                    if(condition)
                        inputType = condition.inputType;
                }
            }
        });
        if(inputType) {
            if (inputType == 'date') {
                return moment(value.value, 'YYYY-MM-DD').format('YYYY-MM-DD');
            } else if (inputType == 'buildings') {
                let string = '';
                value.value.forEach((item, index) => {
                    string += this.getBuildingNameById(item);
                    if (index + 1 < value.value.length)
                        string += ', ';
                });
                return string;
            } else if(inputType == 'possibleValues') {
                let string = '';
                value.value.forEach((item, index) => {
                    string += item.itemName;
                    if (index + 1 < value.value.length)
                        string += ', ';
                });
                return string;
            } else
                return value.value;
        }
    }

    getConditionFieldName(key) {
        if(this.data.fieldsToShow.length > 0) {
            return this.data.fieldsToShow.filter(field => field.key == key)[0].name;
        }
    }

    getConditionName(key, condition) {
        if(this.data.fieldsToShow.length > 0) {
            const conditions = this.data.fieldsToShow.filter(cond => cond.key == key)[0].conditions;
            return conditions.filter(cond => cond.id == condition)[0].name;
        }
    }

    getConditionsForSending() {
        return this.data.conditions.map(cond => {
            let values = [];
            if(cond.value) {
                let inputType = '';
                this.sections.forEach(section => {
                    if(section.id == this.chosenSection.id) {
                        const field = section.fields.find(field => field.key == cond.key);
                        if(field) {
                            const condition = field.conditions.find(condition => (condition.id == cond.condition));
                            if(condition)
                                inputType = condition.inputType;
                        }
                    }
                });

                if(inputType == 'date')
                    values = [moment(cond.value).format('YYYY-MM-DD')];
                else if(inputType == 'buildings')
                    values = cond.value.map(item => (+item));
                else {
                    if(typeof cond.value != 'object')
                        values = [cond.value];
                    else
                        values = cond.value;
                }
            } else
                values = undefined;

            return {
                key: cond.key,
                condition: cond.condition,
                values: values,
                isRuntime: cond.isRuntime
            };
        });
    }

    showAllFields() {
        this.chosenSection.fields.forEach(field => this.data.fieldsToShow.push(field));
        this.chosenSection.fields = [];
        this.reportGeneratorService.modalInfoWasChanged = true;
    }

    getFieldsWithConditions(): Array<any> {
        return this.data.fieldsToShow.filter(cond => cond.conditions.length > 0);
    }

    addSorting(): void {
        if(this.data.fieldsToShow.length > 0) {
            if(!this.data.orderBy)
                this.data.orderBy = this.data.fieldsToShow[0].key;
            if(!this.data.orderByDirection)
                this.data.orderByDirection = 'asc';
            this.showSorting = true;
        }
    }

    hideSorting(): void {
        this.data.orderBy = '';
        this.data.orderByDirection = '';
        this.showSorting = false;
    }

    addConditions(): void {
        if(this.data.fieldsToShow.length > 0)
            this.showConditions = true;
    }

    getBuildingNameById(id): string {
        let name = '';

        this.reportGeneratorService.buildings.filter(building => {
            if (building.id == id)
                name = building.name;
        });

        return name;
    }

    onConditionChange(event) {
        const conditions = this.getFieldsWithConditions().find(cond => cond.key == this.newCondition.key).conditions;
        const inputType = conditions.find(cond => cond.id == event.target.value).inputType;
        if(inputType == 'buildings')
            (<any>this.newCondition).value = [];
        else if(this.getFieldType() == 'possibleValues') {
            this.possibleValues = this.getPossibleValues();
            (<any>this.newCondition).value = [];
        } else
            this.newCondition.value = '';
    }

    ngOnDestroy() {
        if(!this.reportGeneratorService.showReportNameModal)
            this.reportGeneratorService.templateInTemplateModal = null;
        this.reportGeneratorService.modalInfoWasChanged = false;
        this.reportGeneratorService.isReportModalShow = false;
        if(this.dragulaService.find('bag-fields'))
            this.dragulaService.destroy('bag-fields');
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        setTimeout(() => this.hideModal(), 100);
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/report-generator/report-generator-modal/report-generator-modal.component.ts